/**
 * What a sidebar drag *means* (parity cards **P-161**, **P-279**, **P-280**, **P-281**; spec
 * M0-F4 §S8).
 *
 * §S8: *"Drag-and-drop of a board into a folder / of a folder into a folder is **B1's**
 * mutation; F4 supplies the drop targets and the drag preview."* B1's half has worked since the
 * round that shipped `boards.move` and `folders.update`; P-161 built F4's half.
 *
 * The **decision** lives here, on its own, because it is arithmetic over the flattened rows and
 * it is the part worth a unit test: `SidebarTree` owns the gesture, this owns what it writes.
 *
 * ## P-280 — a drop is a *band*, not a row
 *
 * The first version of this file took only "which row is the drag over", and answered "dropped
 * on a folder → go **into** it". Meanwhile `SortableContext`'s `verticalListSortingStrategy`
 * opened a gap **between** the rows under the pointer, which is the universal "it will land
 * here" indicator. So the picture said *between* and the write said *inside*: the user dragged
 * `MK` next to `PDT` to reorder it and got `MK` nested inside `PDT` — twice in one day, on the
 * live Main workspace (`FIN` into `LGS` the second time).
 *
 * The fix is that a drop target is `{ overKey, band }` and the **same** value both paints the
 * indicator and produces the plan. The bands are monday's, measured as fractions of the row:
 *
 * ```
 *   ┌──────────────┐  ← top 25 %      `before` — insert above this row, in *its* container
 *   │              │     middle 50 %  `into`   — nest, and only when the row is a folder
 *   └──────────────┘  ← bottom 25 %   `after`  — insert below this row, in *its* container
 * ```
 *
 * A **leaf** row has no inside, so its middle 50 % splits at the midpoint into `before` /
 * `after` (`bandFor(…, { canNest: false })`) — "never *into* for boards".
 *
 * ## P-279 — the way back to the workspace's top level
 *
 * `before` / `after` on a **root** row means "into the workspace itself", because a row's band
 * inserts into *that row's* container and a root row's container is `null`. That is monday's
 * "drop between top-level rows / above the first", and it is what a nested folder had no way of
 * doing: every drop either nested it deeper or reordered it inside the parent it was stuck in.
 * `ROOT_DROP_KEY` is the same target for the empty space below the last row — the tree's
 * "…and at the end", which no row's band can express.
 *
 * ## P-281 — a board reorders too
 *
 * P-280 left half a gesture: a band beside a board painted the line, but `boards.move` carried
 * no ordering key, so a board could only ever be *re-filed* into another container and a board
 * dropped between two rows of its own folder wrote nothing. `boards.move` now takes a
 * `position` exactly as `folders.update` does, and a band on a board is the same sentence a
 * band on a folder is: **this container, this gap**. A board is ordered among the container's
 * boards (`positionForInsert(…, 'board', …)`), which is its own scale.
 *
 * A drop that would change nothing, or that would put a folder inside itself, returns `null`:
 * the tree must never send a mutation a drop did not ask for.
 */
import { positionBetween } from '@elizmonday/shared';
import type { FlatRow } from '../../modules/shell/tree';

export type SidebarDropPlan =
  | { kind: 'move-board'; boardId: string; folderId: string | null; position?: string }
  | { kind: 'reorder-board'; boardId: string; position: string }
  | { kind: 'move-folder'; folderId: string; parentFolderId: string | null; position?: string }
  | { kind: 'reorder-folder'; folderId: string; position: string }
  | null;

/** Where in a row the pointer is, and therefore what the drop means (P-280). */
export type DropBand = 'before' | 'into' | 'after';

/** The droppable under the last row: the workspace's own top level, at the end (P-279). */
export const ROOT_DROP_KEY = 'sidebar-root-drop';

/**
 * The share of a row, top and bottom, that reads as "between the rows" rather than "on it".
 * monday's own bands are roughly a quarter each; a 28 px sidebar row therefore gives 7 px of
 * insertion band at each edge, which is a target a hand can hit.
 */
export const BAND_EDGE = 0.25;

/**
 * The band a pointer at `pointerY` is in, for a row occupying `[top, top + height)`.
 *
 * `canNest` is false for a row nothing can go inside — a board, a doc, a dashboard — and then
 * the whole row is an insertion band split at its midpoint, never `into`.
 */
export function bandFor(
  pointerY: number,
  rect: { top: number; height: number },
  { canNest }: { canNest: boolean },
): DropBand {
  if (rect.height <= 0) return canNest ? 'into' : 'before';
  const ratio = (pointerY - rect.top) / rect.height;
  if (!canNest) return ratio < 0.5 ? 'before' : 'after';
  if (ratio < BAND_EDGE) return 'before';
  if (ratio > 1 - BAND_EDGE) return 'after';
  return 'into';
}

/** The container a row *sits in* — the folder it belongs to, `null` at the workspace's level. */
export function containerOf(row: FlatRow): string | null {
  return row.node.kind === 'folder'
    ? (row.node.folder.parentFolderId ?? null)
    : (row.node.leaf.folderId ?? null);
}

/** Every folder id inside `folderId`, itself included — the cycle guard for a folder drop. */
function subtreeFolderIds(rows: readonly FlatRow[], folderId: string): Set<string> {
  const children = new Map<string | null, string[]>();
  for (const row of rows) {
    if (row.node.kind !== 'folder') continue;
    const parent = row.node.folder.parentFolderId;
    children.set(parent, [...(children.get(parent) ?? []), row.node.id]);
  }
  const ids = new Set<string>([folderId]);
  const queue = [folderId];
  while (queue.length > 0) {
    const next = queue.shift()!;
    for (const child of children.get(next) ?? []) {
      if (ids.has(child)) continue;
      ids.add(child);
      queue.push(child);
    }
  }
  return ids;
}

/**
 * The position a row of kind `among` needs to sit at row index `insertAt` inside `container`.
 *
 * Row indices are the right currency even though a folder's descendants sit between it and its
 * next sibling: a sibling's *header* row index is what is compared, and every descendant of the
 * folder above the gap has a lower index than the sibling below it.
 *
 * **P-281** made it take the kind. A board is ordered among the container's **boards**, not
 * among its folders: the tree paints every folder of a level before every leaf of it
 * (`shell/tree.ts#buildTree`) and `boards.position` / `folders.position` are two independent
 * scales, so measuring a board's new key against folder positions would place it anywhere.
 * Docs and dashboards are left out of the neighbour list on purpose — `shell.sidebar` sends
 * them a *name* as their `position` (they have no stored order), which is not on this scale
 * either and would make `positionBetween` throw.
 *
 * Returns `null` when the neighbours have no gap left, or when either of them carries a
 * position this arithmetic cannot read — a rebalance, which is B1's job and not something a
 * drop may improvise.
 */
function positionForInsert(
  rows: readonly FlatRow[],
  among: 'folder' | 'board',
  excludeId: string,
  container: string | null,
  insertAt: number,
): string | null {
  const siblings = rows
    .map((row, index) => ({ node: row.node, index }))
    .flatMap((entry) => {
      // The kind is tested before the id: a folder and a board **may share an id**
      // (`FlatRow.key` exists for that reason), so an id-only exclusion drops the wrong row.
      if (among === 'folder') {
        if (entry.node.kind !== 'folder' || entry.node.id === excludeId) return [];
        if ((entry.node.folder.parentFolderId ?? null) !== container) return [];
        return [{ index: entry.index, position: entry.node.folder.position }];
      }
      if (entry.node.kind !== 'leaf' || entry.node.leaf.type !== 'board') return [];
      if (entry.node.id === excludeId) return [];
      if ((entry.node.leaf.folderId ?? null) !== container) return [];
      return [{ index: entry.index, position: entry.node.leaf.position }];
    });

  const before = siblings.filter((entry) => entry.index < insertAt);
  const after = siblings.filter((entry) => entry.index >= insertAt);
  const prev = before.length > 0 ? before[before.length - 1]!.position : null;
  const next = after.length > 0 ? after[0]!.position : null;
  try {
    return positionBetween(prev, next);
  } catch {
    return null;
  }
}

/** An insertion band: the dragged row joins `container`, at `insertAt` in row order. */
function planInsert(
  rows: readonly FlatRow[],
  active: FlatRow,
  container: string | null,
  insertAt: number,
): SidebarDropPlan {
  if (active.node.kind === 'leaf') {
    /*
     * **P-281.** `boards.move` carries a `position` now, exactly as `folders.update` does, so
     * an insertion band means for a board what it has always meant for a folder: land in this
     * container, in this gap. Before the card it carried `workspaceId` / `folderId` only, a
     * band could therefore only *re-file* a board, and a board dropped between two rows of the
     * folder it was already in wrote nothing at all.
     */
    if (active.node.leaf.type !== 'board') return null;
    const leaf = active.node.leaf;
    const position = positionForInsert(rows, 'board', leaf.id, container, insertAt);
    if ((leaf.folderId ?? null) === container) {
      // Same container: a pure reorder, and the gap it is already in writes nothing.
      if (position === null || position === leaf.position) return null;
      return { kind: 'reorder-board', boardId: leaf.id, position };
    }
    // A different container: re-file, and place it in the gap when the gap can be measured.
    return {
      kind: 'move-board',
      boardId: leaf.id,
      folderId: container,
      ...(position === null ? {} : { position }),
    };
  }

  const folder = active.node.folder;
  // Into its own subtree is a cycle; the service refuses it (`FOLDER_CYCLE`) and so does this.
  if (container !== null && subtreeFolderIds(rows, folder.id).has(container)) return null;

  const position = positionForInsert(rows, 'folder', folder.id, container, insertAt);
  if (position === null) return null;
  const sameParent = (folder.parentFolderId ?? null) === container;
  if (sameParent) {
    if (position === folder.position) return null;
    return { kind: 'reorder-folder', folderId: folder.id, position };
  }
  return { kind: 'move-folder', folderId: folder.id, parentFolderId: container, position };
}

/**
 * What the drop writes — from exactly the `{ overKey, band }` the indicator painted (P-280).
 *
 * `band` defaults to `into`, which is the pre-P-280 meaning of "dropped on this row" and keeps
 * a caller that has no pointer (a test, a keyboard path) on the old, explicit behaviour.
 */
export function planSidebarDrop(
  rows: readonly FlatRow[],
  activeKey: string,
  overKey: string,
  band: DropBand = 'into',
): SidebarDropPlan {
  const activeIndex = rows.findIndex((row) => row.key === activeKey);
  if (activeIndex < 0) return null;
  const active = rows[activeIndex]!;

  /* ── the workspace's own level, below the last row (P-279) ───────────────── */
  if (overKey === ROOT_DROP_KEY) return planInsert(rows, active, null, rows.length);

  if (activeKey === overKey) return null;
  const overIndex = rows.findIndex((row) => row.key === overKey);
  if (overIndex < 0) return null;
  const over = rows[overIndex]!;

  /* ── an insertion band: between the rows, in the over row's own container ── */
  if (band !== 'into') {
    // Dropping a folder just above or just below itself is the gesture landing where it
    // started; so is a band on a row inside the folder being dragged.
    if (
      active.node.kind === 'folder' &&
      over.node.kind === 'folder' &&
      subtreeFolderIds(rows, active.node.id).has(over.node.id)
    ) {
      return null;
    }
    return planInsert(rows, active, containerOf(over), band === 'before' ? overIndex : overIndex + 1);
  }

  /* ── `into`: a leaf can only be filed; a doc or a dashboard has no `move` yet ── */
  if (active.node.kind === 'leaf') {
    if (active.node.leaf.type !== 'board') return null;
    const target = over.node.kind === 'folder' ? over.node.id : over.node.leaf.folderId;
    if ((active.node.leaf.folderId ?? null) === (target ?? null)) return null;
    return { kind: 'move-board', boardId: active.node.id, folderId: target ?? null };
  }

  /* ── `into`: a folder nests inside another folder ────────────────────────── */
  const folder = active.node.folder;
  if (over.node.kind === 'folder') {
    if (subtreeFolderIds(rows, folder.id).has(over.node.id)) return null;
    if ((folder.parentFolderId ?? null) === over.node.id) return null;
    return { kind: 'move-folder', folderId: folder.id, parentFolderId: over.node.id };
  }

  /*
   * A folder dropped on the **body** of a leaf: a leaf has no inside, so this only reaches a
   * caller that passed `into` by hand. It means the leaf's own container — the same answer the
   * `before` band on that row gives, one row earlier.
   */
  return planInsert(rows, active, containerOf(over), overIndex);
}

/**
 * The keyboard equivalent of dragging a folder back out to the workspace level (P-279).
 *
 * Returns the plan `Shift+ArrowLeft` on a focused folder row performs, or `null` when that row
 * is not a nested folder and the key therefore means nothing.
 */
export function planUnnestFolder(rows: readonly FlatRow[], rowKey: string | null): SidebarDropPlan {
  const row = rows.find((entry) => entry.key === rowKey);
  if (!row || row.node.kind !== 'folder') return null;
  if ((row.node.folder.parentFolderId ?? null) === null) return null;
  return planInsert(rows, row, null, rows.length);
}
